import React, { useMemo } from 'react';
import { Skeleton, Box } from '@mui/material';

/**
 * Contextual skeleton loaders that match the content layout
 * of various page sections. Each variant mimics the actual
 * content structure so loading feels seamless.
 */

/** Calendar page skeleton — mimics the weekly calendar grid */
export function CalendarSkeleton({ message = 'Preparing your schedule...' }: { message?: string }) {
  return (
    <Box sx={{ p: 2, maxWidth: 1200, mx: 'auto' }}>
      {/* Header row */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
        <Skeleton variant="rounded" width={180} height={36} />
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Skeleton variant="rounded" width={80} height={36} />
          <Skeleton variant="rounded" width={80} height={36} />
          <Skeleton variant="rounded" width={80} height={36} />
        </Box>
      </Box>
      {/* Contextual message */}
      <Box sx={{ textAlign: 'center', mb: 2 }}>
        <p className="text-sm text-gray-400 dark:text-gray-500 animate-fade-in">
          {message}
        </p>
      </Box>
      {/* Day headers */}
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 1, mb: 1 }}>
        {Array.from({ length: 7 }).map((_, i) => (
          <Skeleton key={i} variant="text" width="80%" height={24} />
        ))}
      </Box>
      {/* Time slots */}
      {Array.from({ length: 6 }).map((_, row) => (
        <Box key={row} sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 1, mb: 0.5 }}>
          {Array.from({ length: 7 }).map((_, col) => (
            <Skeleton
              key={col}
              variant="rounded"
              height={48}
              sx={{ borderRadius: 1 }}
            />
          ))}
        </Box>
      ))}
    </Box>
  );
}

/** Briefing panel skeleton — mimics the Today's Briefing layout */
export function BriefingSkeleton({ message = "Gathering today's sessions..." }: { message?: string }) {
  return (
    <Box sx={{ p: 2 }}>
      <Skeleton variant="text" width="60%" height={32} />
      <Box sx={{ textAlign: 'center', my: 1 }}>
        <p className="text-xs text-gray-400 dark:text-gray-500 animate-fade-in">
          {message}
        </p>
      </Box>
      <Skeleton variant="text" width="80%" height={24} sx={{ mt: 1 }} />
      <Skeleton variant="rounded" height={80} sx={{ mt: 2, borderRadius: 1 }} />
      <Skeleton variant="text" width="40%" height={24} sx={{ mt: 2 }} />
      <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
        <Skeleton variant="rounded" width={100} height={32} sx={{ borderRadius: 2 }} />
        <Skeleton variant="rounded" width={80} height={32} sx={{ borderRadius: 2 }} />
      </Box>
    </Box>
  );
}

/** Dashboard/generic section skeleton — for data tables, settings, etc. */
export function SectionSkeleton({ message = 'Loading...', rows = 4 }: { message?: string; rows?: number }) {
  const rowWidths = useMemo(
    () => Array.from({ length: rows }, () => ({
      primary: `${60 + Math.random() * 30}%`,
      secondary: `${40 + Math.random() * 20}%`,
    })),
    [rows]
  );

  return (
    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 300, flexDirection: 'column', gap: 2 }}>
      <Box sx={{ width: '100%', maxWidth: 600, px: 2 }}>
        {/* Title area */}
        <Skeleton variant="text" width="50%" height={32} sx={{ mb: 1 }} />
        {/* Contextual message */}
        <p className="text-sm text-gray-400 dark:text-gray-500 animate-fade-in mb-3">
          {message}
        </p>
        {/* Content rows */}
        {rowWidths.map((widths, i) => (
          <Box key={i} sx={{ display: 'flex', gap: 2, mb: 1.5 }}>
            <Skeleton variant="circular" width={36} height={36} />
            <Box sx={{ flex: 1 }}>
              <Skeleton variant="text" width={widths.primary} height={20} />
              <Skeleton variant="text" width={widths.secondary} height={16} />
            </Box>
          </Box>
        ))}
      </Box>
    </Box>
  );
}

/** Dialog/modal content skeleton */
export function DialogSkeleton({ rows = 3 }: { rows?: number }) {
  const dialogWidths = useMemo(
    () => Array.from({ length: rows }, () => `${70 + Math.random() * 25}%`),
    [rows]
  );

  return (
    <Box sx={{ p: 2 }}>
      {dialogWidths.map((width, i) => (
        <Skeleton key={i} variant="text" width={width} height={24} sx={{ mb: 1 }} />
      ))}
      <Box sx={{ display: 'flex', gap: 1, mt: 2, justifyContent: 'flex-end' }}>
        <Skeleton variant="rounded" width={80} height={36} sx={{ borderRadius: 1 }} />
        <Skeleton variant="rounded" width={100} height={36} sx={{ borderRadius: 1 }} />
      </Box>
    </Box>
  );
}

/** Table data skeleton */
export function TableSkeleton({ rows = 5, columns = 4, message = 'Loading...' }: { rows?: number; columns?: number; message?: string }) {
  return (
    <Box sx={{ width: '100%' }}>
      <p className="text-sm text-gray-400 dark:text-gray-500 animate-fade-in mb-2 text-center">
        {message}
      </p>
      {/* Header */}
      <Box sx={{ display: 'grid', gridTemplateColumns: `repeat(${columns}, 1fr)`, gap: 2, mb: 1, px: 2, py: 1 }}>
        {Array.from({ length: columns }).map((_, i) => (
          <Skeleton key={i} variant="text" height={24} />
        ))}
      </Box>
      {/* Rows */}
      {Array.from({ length: rows }).map((_, rowIdx) => (
        <Box
          key={rowIdx}
          sx={{
            display: 'grid',
            gridTemplateColumns: `repeat(${columns}, 1fr)`,
            gap: 2,
            px: 2,
            py: 1.5,
            borderTop: '1px solid',
            borderColor: 'divider',
          }}
        >
          {Array.from({ length: columns }).map((_, colIdx) => (
            <Skeleton key={colIdx} variant="text" height={20} />
          ))}
        </Box>
      ))}
    </Box>
  );
}

/** Analytics page skeleton — mimics the metrics cards + table layout */
export function AnalyticsSkeleton({ message = 'Loading analytics...' }: { message?: string }) {
  return (
    <Box sx={{ p: 3, maxWidth: 1200, mx: 'auto' }}>
      {/* Header row */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Skeleton variant="rounded" width={220} height={36} />
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Skeleton variant="rounded" width={120} height={36} />
          <Skeleton variant="circular" width={36} height={36} />
        </Box>
      </Box>
      {/* Contextual message */}
      <Box sx={{ textAlign: 'center', mb: 2 }}>
        <p className="text-sm text-gray-400 dark:text-gray-500 animate-fade-in">
          {message}
        </p>
      </Box>
      {/* Metrics cards row */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' }, gap: 2, mb: 3 }}>
        {Array.from({ length: 4 }).map((_, i) => (
          <Box key={i} sx={{ p: 2, borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
            <Skeleton variant="text" width="60%" height={18} />
            <Skeleton variant="text" width="40%" height={32} sx={{ mt: 0.5 }} />
            <Skeleton variant="text" width="50%" height={16} sx={{ mt: 0.5 }} />
          </Box>
        ))}
      </Box>
      {/* Tab bar */}
      <Box sx={{ display: 'flex', gap: 2, mb: 3, borderBottom: '1px solid', borderColor: 'divider', pb: 1, overflowX: 'auto' }}>
        {Array.from({ length: 5 }).map((_, i) => (
          <Skeleton key={i} variant="rounded" width={100} height={28} sx={{ flexShrink: 0 }} />
        ))}
      </Box>
      {/* Table skeleton */}
      <Box sx={{ borderRadius: 2, border: '1px solid', borderColor: 'divider', overflow: 'hidden' }}>
        {/* Table header */}
        <Box sx={{ display: { xs: 'none', md: 'grid' }, gridTemplateColumns: 'repeat(5, 1fr)', gap: 2, px: 2, py: 1.5, bgcolor: 'action.hover' }}>
          {Array.from({ length: 5 }).map((_, i) => (
            <Skeleton key={i} variant="text" height={20} />
          ))}
        </Box>
        {/* Table rows */}
        {Array.from({ length: 5 }).map((_, rowIdx) => (
          <Box
            key={rowIdx}
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: 'repeat(5, 1fr)' },
              gap: { xs: 1, md: 2 },
              px: 2,
              py: 1.5,
              borderTop: '1px solid',
              borderColor: 'divider',
            }}
          >
            {Array.from({ length: 5 }).map((_, colIdx) => (
              <Skeleton key={colIdx} variant="text" height={20} />
            ))}
          </Box>
        ))}
      </Box>
    </Box>
  );
}

/** Timesheet page skeleton — mimics date filters + staff table layout */
export function TimesheetSkeleton({ message = 'Loading timesheet data...' }: { message?: string }) {
  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto' }}>
      {/* Contextual message */}
      <Box sx={{ textAlign: 'center', mb: 2 }}>
        <p className="text-sm text-gray-400 dark:text-gray-500 animate-fade-in">
          {message}
        </p>
      </Box>
      {/* Date filter card */}
      <Box sx={{ p: 2, mb: 3, borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
          <Skeleton variant="rounded" width={160} height={40} />
          <Skeleton variant="rounded" width={160} height={40} />
          <Box sx={{ ml: 'auto', display: 'flex', gap: 1 }}>
            <Skeleton variant="rounded" width={70} height={36} />
            <Skeleton variant="rounded" width={70} height={36} />
          </Box>
        </Box>
      </Box>
      {/* Staff summary card — hidden on mobile where the table doesn't fit */}
      <Box sx={{ display: { xs: 'none', md: 'block' }, p: 2, mb: 3, borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Skeleton variant="text" width={180} height={28} />
          <Skeleton variant="rounded" width={240} height={28} />
        </Box>
        {/* Staff table rows */}
        {Array.from({ length: 4 }).map((_, i) => (
          <Box key={i} sx={{ display: 'flex', gap: 2, py: 1.5, borderTop: '1px solid', borderColor: 'divider', alignItems: 'center' }}>
            <Skeleton variant="text" width="25%" height={20} />
            <Skeleton variant="rounded" width={70} height={22} sx={{ borderRadius: 8 }} />
            <Skeleton variant="text" width="12%" height={20} />
            <Skeleton variant="text" width="12%" height={20} />
            <Skeleton variant="text" width="12%" height={20} />
            <Skeleton variant="rounded" width={70} height={22} sx={{ borderRadius: 8 }} />
          </Box>
        ))}
      </Box>
      {/* Practitioner detail cards */}
      {Array.from({ length: 2 }).map((_, i) => (
        <Box key={i} sx={{ p: 2, mb: 2, borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            <Skeleton variant="circular" width={40} height={40} />
            <Box sx={{ flex: 1 }}>
              <Skeleton variant="text" width="30%" height={22} />
              <Skeleton variant="text" width="20%" height={16} />
            </Box>
          </Box>
          <Skeleton variant="rounded" height={60} sx={{ borderRadius: 1 }} />
        </Box>
      ))}
    </Box>
  );
}

/** Admin settings page skeleton — mimics sidebar + content panel layout */
export function SettingsSkeleton({ message = 'Loading settings...' }: { message?: string }) {
  return (
    <Box sx={{ p: 3, maxWidth: 1400, mx: 'auto' }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Skeleton variant="text" width={200} height={40} />
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Skeleton variant="rounded" width={160} height={36} />
          <Skeleton variant="rounded" width={120} height={36} />
        </Box>
      </Box>
      {/* Progress banner */}
      <Box sx={{ p: 2, mb: 3, borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <Skeleton variant="circular" width={22} height={22} />
          <Skeleton variant="text" width={140} height={24} />
          <Skeleton variant="rounded" width={60} height={22} sx={{ borderRadius: 8 }} />
        </Box>
        <Skeleton variant="rounded" height={6} sx={{ borderRadius: 3, mb: 1.5 }} />
        <Box sx={{ display: 'flex', gap: 0.75 }}>
          {Array.from({ length: 6 }).map((_, i) => (
            <Skeleton key={i} variant="rounded" width={90} height={24} sx={{ borderRadius: 8 }} />
          ))}
        </Box>
      </Box>
      {/* Sidebar + content */}
      <Box sx={{ display: 'flex', gap: 3 }}>
        {/* Sidebar — hidden on mobile (actual page uses a drawer) */}
        <Box sx={{ display: { xs: 'none', md: 'block' }, width: 280, flexShrink: 0 }}>
          {Array.from({ length: 8 }).map((_, i) => (
            <Box key={i} sx={{ mb: 1 }}>
              <Skeleton variant="text" width="70%" height={28} />
              {i < 3 && (
                <Box sx={{ pl: 2, mt: 0.5 }}>
                  <Skeleton variant="text" width="80%" height={22} />
                  <Skeleton variant="text" width="60%" height={22} />
                </Box>
              )}
            </Box>
          ))}
        </Box>
        {/* Content area */}
        <Box sx={{ flex: 1 }}>
          <p className="text-sm text-gray-400 dark:text-gray-500 animate-fade-in mb-3">
            {message}
          </p>
          <Skeleton variant="text" width="40%" height={32} sx={{ mb: 2 }} />
          {Array.from({ length: 5 }).map((_, i) => (
            <Box key={i} sx={{ mb: 2 }}>
              <Skeleton variant="text" width="30%" height={18} sx={{ mb: 0.5 }} />
              <Skeleton variant="rounded" height={40} sx={{ borderRadius: 1 }} />
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  );
}

/** Client portal tab skeleton */
export function ClientPortalSkeleton({ message = 'Loading...' }: { message?: string }) {
  return (
    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400, flexDirection: 'column', gap: 2 }}>
      <Box sx={{ width: '100%', maxWidth: 800, px: 3 }}>
        <p className="text-sm text-gray-400 dark:text-gray-500 animate-fade-in text-center mb-4">
          {message}
        </p>
        {/* Card skeletons */}
        {Array.from({ length: 3 }).map((_, i) => (
          <Box
            key={i}
            sx={{
              p: 2,
              mb: 2,
              borderRadius: 2,
              border: '1px solid',
              borderColor: 'divider',
            }}
          >
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
              <Skeleton variant="circular" width={40} height={40} />
              <Box sx={{ flex: 1 }}>
                <Skeleton variant="text" width="60%" height={22} />
                <Skeleton variant="text" width="40%" height={18} />
              </Box>
              <Skeleton variant="rounded" width={80} height={32} sx={{ borderRadius: 2 }} />
            </Box>
          </Box>
        ))}
      </Box>
    </Box>
  );
}
